// The app's opening (Gianluca, 2026-11-03): while the companion's window
// waits for the session, the mark plays big on the dark canvas, the gear
// coming down and the two chocks going in against the tyre, then the name
// and the slogan rise, and a line of runway lights chases until the page is
// ready. It stays at least long enough for the chocks to land, then fades.
// Shown under the companion's shell alone: a browser visitor has the page
// itself at the first paint (decision 136).

import { useEffect, useRef, useState } from "react";

export type SplashPhase = "shown" | "leaving" | "gone";

/** At least this long (the mark's play), whatever the session's speed. */
export const SPLASH_MINIMUM_MS = 1700;
/** The fade at the end. */
export const SPLASH_FADE_MS = 320;

/**
 * The phase of the opening: shown while `pending` and until the minimum
 * time is up, then leaving (the fade), then gone for good.
 */
export function useSplash(pending: boolean, minimumMs = SPLASH_MINIMUM_MS): SplashPhase {
  const [phase, setPhase] = useState<SplashPhase>("shown");
  const startedAt = useRef<number>(Date.now());
  useEffect(() => {
    if (phase !== "shown" || pending) return;
    const left = Math.max(0, minimumMs - (Date.now() - startedAt.current));
    const timer = window.setTimeout(() => setPhase("leaving"), left);
    return () => window.clearTimeout(timer);
  }, [pending, minimumMs, phase]);
  useEffect(() => {
    if (phase !== "leaving") return;
    const timer = window.setTimeout(() => setPhase("gone"), SPLASH_FADE_MS);
    return () => window.clearTimeout(timer);
  }, [phase]);
  return phase;
}

export const SLOGAN = "The shared aircraft that remembers";

export function Splash({ phase }: { phase: Exclude<SplashPhase, "gone"> }) {
  return (
    <div className={`splash${phase === "leaving" ? " is-leaving" : ""}`} role="status" aria-live="polite" aria-label={`ShariFly is opening. ${SLOGAN}`} data-testid="splash" data-phase={phase}>
      <svg className="splash-mark" viewBox="0 0 256 256" aria-hidden="true" data-testid="splash-mark">
        <rect width="256" height="256" rx="56" fill="var(--color-accent)" />
        <g transform="translate(33.3 34.1) scale(0.74)">
          <g className="splash-leg">
            <rect x="100" y="12" width="56" height="16" rx="8" fill="#ffffff" />
            <rect x="114" y="20" width="28" height="112" fill="#ffffff" />
            <path fill="none" stroke="#ffffff" strokeWidth="14" strokeLinecap="round" d="M120 74 L74 32" />
            <path fill="#ffffff" fillRule="evenodd" d="M128 112 A48 48 0 1 1 127.9 112 Z M128 148 A12 12 0 1 0 128.1 148 Z" />
          </g>
          <rect x="24" y="208" width="208" height="20" fill="#ffffff" />
          <path className="splash-chock splash-chock-left" fill="#f4a62a" d="M95.4 208 A58 58 0 0 1 72.9 164 L47.5 208 Z" />
          <path className="splash-chock splash-chock-right" fill="#f4a62a" d="M160.6 208 A58 58 0 0 0 183.1 164 L208.5 208 Z" />
        </g>
      </svg>
      <div className="splash-name">ShariFly</div>
      <div className="splash-slogan">{SLOGAN}</div>
      <div className="splash-lights" aria-hidden="true">
        {Array.from({ length: 7 }, (_, i) => (
          <i key={i} style={{ animationDelay: `${i * 140}ms` }} />
        ))}
      </div>
    </div>
  );
}
